import { Component, OnInit, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { DateRangePickerComponent } from '../../shared/components/date-range-picker/date-range-picker.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { InstallmentBadgeComponent } from '../../shared/components/installment-badge/installment-badge.component';
import { HttpClient } from '@angular/common/http';
import { HotelService } from '../../services/hotel.service';
import { SearchService, SearchRequest, SearchResponse, UserSearchCriteria, SearchFilterItem } from '../../services/search.service';
import { PopularService, PopularItem } from '../../services/popular.service';
import { HotelFilterService, HotelFilterRequest, HotelFilterResult } from '../../services/hotel-filter.service';
import { HotelFilterSidebarComponent } from '../../shared/components/hotel-filter-sidebar/hotel-filter-sidebar.component';
import { toLocalISOString } from '../../core/utils/date.util';
import { Subscription, Observable, of, catchError, throwError, Subject, merge } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { criteriaToQueryParams, queryParamsToCriteria, queryParamsEqual } from './hotel-list-url.util';

interface Hotel {
  id: string;
  key: string;
  name: string;
  location: string;
  rating: number;
  reviews: number;
  price: number;
  originalPrice: number | null;
  discountTitle: string | null;
  discountRate: number | null;
  image: string;
  stars: number;
  checkIn: string;
  checkOut: string;
  roomType: string;
  mealType: string;
  facilities: string[];
  cancellationPolicy?: string;
  cancellationOptionDate?: string;
  nonRefundable?: boolean;
  quotaStatus: string | null;
  minNight: number | null;
  roomCount: number | null;
  restaurantCount: number | null;
  barCount: number | null;
  themes: string[];
}

@Component({
  selector: 'app-hotel-list',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    RouterModule,
    DateRangePickerComponent,
    LoadingComponent,
    InstallmentBadgeComponent,
    HotelFilterSidebarComponent
  ],
  templateUrl: './hotel-list.component.html',
  styleUrl: './hotel-list.component.css'
})
export class HotelListComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(FormBuilder);
  private readonly apiUrl = environment.apiUrl;
  
  hotels: Hotel[] = [];
  allHotels: Hotel[] = []; // Tüm oteller (client-side pagination için)
  searchResults: SearchResponse | null = null;
  isLoading = true;
  private searchSubscription: Subscription | undefined;
  private dateSubscription?: Subscription;
  
  // Minimum gece kısıtlaması
  hasMinNightRestriction = false;
  minNightCount = 0;
  minNightMessage = '';
  alternativeRoomGroups: any[] = [];

  // Pagination state
  currentPage = 1;
  totalPages = 1;
  totalCount = 0;
  pageSize = 10;
  requestKey: string | null = null;
  isPaginationLoading = false;
  useClientSidePagination = false; // API pagination çalışmazsa client-side kullan

  // Tarihsiz arama kontrolü ve uyarı
  showDateRequiredWarning = false;
  isDatelessSearch = false; // Tarihsiz arama yapıldı mı (fiyat gösterilip gösterilmeyeceğini belirler)
  originalWasDateless = false; // Orijinal saved criteria'da tarih yoktu
  readonly MAX_ROOMS_WITHOUT_DATE = 1;
  readonly MAX_ADULTS_WITHOUT_DATE = 1;
  maxInstallment: number = 9; // Taksit limiti: Kıbrıs 3, Yurtiçi 9, Yurtdışı 0

  // Form ve UI state
  searchForm!: FormGroup;
  isCalendarOpen = false;
  isGuestSelectorOpen = false;
  isSearching = false; // Arama butonu için loading durumu
  isMobileSearchExpanded = false;
  isMobileLocationOpen = false;
  mobileLocationSearch = '';
  fromNav = false; // Navbar'dan geldi mi
  childrenAges: number[] = [];
  room2ChildrenAges: number[] = []; // 2. oda çocuk yaşları
  filteredLocations$!: Observable<any[]>;
  locationFocus$ = new Subject<string>();
  selectedLocation: any = null;

  // Popüler aramalar — mobil location overlay'inde input boşken gösterilir
  quickSuggestions: any[] = [];
  private isInitializing = true; // Form initialization sırasında valueChanges'ı ignore et

  // Arama parametreleri (eski - backward compatibility için)
  searchParams = {
    location: '',
    checkIn: new Date(),
    checkOut: new Date(new Date().setDate(new Date().getDate() + 7)),
    adults: 2,
    children: 0,
    rooms: 1,
    room2Adults: 1,
    room2Children: 0
  };

  // Popüler lokasyonlar
  popularLocations = [
    { name: 'London', country: 'United Kingdom' },
    { name: 'New York', country: 'United States' },
    { name: 'Paris', country: 'France' },
    { name: 'Madrid', country: 'Spain' },
    { name: 'Santorini', country: 'Greece' }
  ];

  private readonly route = inject(ActivatedRoute);

  // Filtre state
  filterResult: HotelFilterResult | null = null;
  filterLoading = false;
  isFilterSidebarOpen = false;
  currentFilters: SearchFilterItem = {};

  constructor(
    private router: Router,
    private hotelService: HotelService,
    private searchService: SearchService,
    private hotelFilterService: HotelFilterService,
    private popularService: PopularService
  ) {}

  private loadQuickSuggestions(): void {
    // Önce cache kontrol et
    const cached = this.popularService.cachedItems;
    if (cached && cached.length > 0) {
      this.quickSuggestions = this.mapPopularToSuggestions(cached);
      return;
    }
    // Yoksa API'den çek
    this.popularService.getActivePopularItems().subscribe({
      next: items => {
        if (items?.length) this.quickSuggestions = this.mapPopularToSuggestions(items);
      },
      error: () => { /* sessizce atla — popüler öneriler kritik değil */ }
    });
  }

  private mapPopularToSuggestions(items: PopularItem[]): any[] {
    return items.map(item => ({
      id: item.referenceId,
      label: item.label,
      value: item.referenceId,
      parentId: (item.parentId || 0).toString(),
      type: item.type.toLowerCase()
    }));
  }

  ngOnInit() {
    // URL query params'ta arama kriteri varsa öncelikli kaynak olarak kullan
    // (refresh / paylaşım / bookmark senaryolarını çözer)
    const urlCriteria = queryParamsToCriteria(this.route.snapshot.queryParamMap);
    if (urlCriteria) {
      this.searchService.setUserSearchCriteria(urlCriteria);
    }

    if (!this.searchService.getUserSearchCriteria()) {
      this.router.navigate(['/']);
      return;
    }
    this.fromNav = this.route.snapshot.queryParams['from'] === 'nav';
    this.maxInstallment = this.searchService.getMaxInstallmentCount();
    this.initializeSearchForm();
    this.loadQuickSuggestions();
    
    // Orijinal saved criteria'nın tarihsiz olup olmadığını kontrol et
    const savedCriteria = this.searchService.getUserSearchCriteria();
    if (savedCriteria) {
      const checkInDate = savedCriteria.checkInDate ? new Date(savedCriteria.checkInDate) : null;
      const checkOutDate = savedCriteria.checkOutDate ? new Date(savedCriteria.checkOutDate) : null;
      const isDateLess = !checkInDate || !checkOutDate || 
                         (checkInDate && isNaN(checkInDate.getTime())) || 
                         (checkOutDate && isNaN(checkOutDate.getTime()));
      
      if (isDateLess) {
        this.originalWasDateless = true;
        this.isDatelessSearch = true;
        // Form orijinal misafir sayılarını göstermeye devam eder
        // API isteğinde tarihsiz ise 1 yetişkin gönderilir (performSearch'te)
      }
    }
    
    this.initializeLocationAutocomplete();
    
    // Initialization tamamlandı - artık valueChanges'ı dinle
    this.isInitializing = false;
    
    // Tarih değiştiğinde originalWasDateless flag'ini sıfırla
    this.dateSubscription = this.searchForm.get('checkInDate')?.valueChanges.subscribe(checkInDate => {
      if (checkInDate && !this.isInitializing) {
        // Kullanıcı yeni tarih seçti, artık tarihsiz değil
        this.originalWasDateless = false;
        
        const checkOutDate = this.searchForm.get('checkOutDate')?.value;
        if (checkOutDate && checkOutDate <= checkInDate) {
          // Çıkış tarihi giriş tarihinden önce veya aynı ise, giriş tarihinden bir gün sonrasını ayarla
          const newCheckOut = new Date(checkInDate);
          newCheckOut.setDate(newCheckOut.getDate() + 1);
          this.searchForm.get('checkOutDate')?.setValue(newCheckOut);
        }
      }
    });
    
    this.searchSubscription = this.searchService.searchResults$.subscribe(results => {
      if (results) {
        this.searchResults = results;
        this.processSearchResults();
        this.isLoading = false;
        this.syncUrlWithCriteria();

        // Results geldiğinde filterResult yoksa GetFilters'ı tetikle
        // (cache'den / state'ten gelen sonuçlarda performSearch çalışmaz, bu yüzden fallback)
        if (!this.filterResult && !this.filterLoading) {
          this.tryLoadFiltersFromState();
        }
      }
    });

    // URL'den criteria geldi ama henüz sonuç yoksa (fresh tab / refresh / paylaşım linki) search tetikle
    if (urlCriteria && !this.searchResults) {
      // Form hydration tamamlansın, sonraki microtask'ta performSearch çağır
      setTimeout(() => this.performSearch(), 0);
    }
  }

  /**
   * Mevcut arama kriterlerini URL query params'a yazar (refresh/paylaşım için).
   * History'e yeni entry eklemez, mevcut URL'i replace eder.
   */
  private syncUrlWithCriteria(): void {
    const criteria = this.searchService.getUserSearchCriteria();
    if (!criteria) return;
    const queryParams = criteriaToQueryParams(criteria);
    if (queryParamsEqual(this.route.snapshot.queryParams, queryParams)) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.dateSubscription) {
      this.dateSubscription.unsubscribe();
    }
  }

  /**
   * Dropdown menüler dışına tıklandığında kapat
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Takvim dışına tıklandığında kapat (sadece desktop dropdown)
    if (this.isCalendarOpen &&
        !target.closest('.search-field--date') &&
        !target.closest('app-date-range-picker') &&
        !target.closest('.desktop-calendar-panel')) {
      this.isCalendarOpen = false;
    }

    // Misafir seçici dışına tıklandığında kapat
    if (this.isGuestSelectorOpen &&
        !target.closest('.search-field--guests') &&
        !target.closest('.searchMenu-guests__field')) {
      this.isGuestSelectorOpen = false;
    }
  }

  private processSearchResults() {
    if (!this.searchResults) return;

    // Pagination bilgilerini güncelle
    if (this.searchResults.requestKey) {
      this.requestKey = this.searchResults.requestKey;
    }
    
    // ⚠️ Minimum gece kısıtlaması kontrolü
    const altRoomGroups = (this.searchResults as any).alternativeRoomGroup;
    if ((!this.searchResults.items || this.searchResults.items.length === 0) && altRoomGroups?.length > 0) {
      this.hasMinNightRestriction = true;
      this.alternativeRoomGroups = altRoomGroups;
      
      const firstAlt = altRoomGroups[0];
      this.minNightCount = firstAlt.roomGroup?.minNight || 2;
      this.minNightMessage = firstAlt.reasons?.[0]?.text 
        || firstAlt.roomGroup?.messages?.[0]?.text 
        || `Bu otel minimum ${this.minNightCount} gece konaklama gerektirmektedir.`;
      
      
      // Eğer tek bir otel varsa, o otelin detay sayfasına yönlendir
      if (altRoomGroups.length > 0) {
        const productKey = firstAlt.roomGroup?.key;
        if (productKey) {
          const encodedProductKey = encodeURIComponent(productKey);
          this.router.navigate(['/hotel-detail', encodedProductKey]);
          return;
        }
      }
    } else {
      this.hasMinNightRestriction = false;
      this.minNightCount = 0;
      this.minNightMessage = '';
      this.alternativeRoomGroups = [];
    }
    
    const responseData = this.searchResults?.items;

    // Tek otel varsa doğrudan detay sayfasına yönlendir
    if (responseData && responseData.length === 1 && responseData[0].key) {
      const item = responseData[0];
      const hotelData = this.searchResults.hotels?.[item.hotelKey];
      this.searchService.setSelectedHotelPreview({
        name: hotelData?.name || item.hotelName || '',
        image: this.toThumbnail(hotelData?.thumbnailUrl || hotelData?.imageUrl) || '',
        stars: hotelData?.hotelCategoryId || 0,
        location: hotelData?.locationName || '',
        price: item.totalPrice?.value || 0,
        mealType: item.mealType || ''
      });
      const encodedProductKey = encodeURIComponent(item.key);
      this.router.navigate(['/hotel-detail', encodedProductKey]);
      return;
    }

    if (responseData) {
      const uniqueHotels = new Map<string, Hotel>();

      responseData.forEach((item: any) => {
        const hotelData = this.searchResults?.hotels?.[item.hotelKey];
        const hotelId = item.hotelId || item.masterHotelId;

        if (!uniqueHotels.has(hotelId)) {
          // Kampanya bilgilerini API'den al
          const discountInfo = item.discountInformations
            ? Object.values(item.discountInformations)[0] as any
            : null;

          const discountValue = item.discount?.value || 0;
          const totalPriceValue = item.totalPrice?.value || 0;

          // Tema bilgilerini array'e çevir
          const themesArray = hotelData?.themes
            ? Object.values(hotelData.themes) as string[]
            : [];

          uniqueHotels.set(hotelId, {
            id: hotelId,
            key: item.key,
            name: hotelData?.name || '',
            location: hotelData?.address || '',
            rating: hotelData?.hotelPoint || 0,
            reviews: hotelData?.reviewCount || 0,
            price: totalPriceValue,
            originalPrice: discountValue > 0 ? discountValue : null,
            discountTitle: discountInfo?.title || null,
            discountRate: discountInfo?.discountRate || null,
            image: this.toThumbnail(hotelData?.images?.[0]?.url || hotelData?.thumbnailUrl) || 'assets/img/hotels/default.png',
            stars: hotelData?.hotelCategoryId || 0,
            checkIn: item.checkInDate,
            checkOut: item.checkOutDate,
            roomType: item.rooms?.[0]?.accomodationType,
            mealType: item.rooms?.[0]?.mealType,
            facilities: hotelData?.facilityDetails?.slice(0, 5).map((f: any) => f.name) || [],
            cancellationPolicy: item.rooms?.[0]?.cancellationPolicies?.name,
            cancellationOptionDate: item.rooms?.[0]?.cancellationPolicies?.cancellationPolicyDetail?.[0]?.optionDate,
            nonRefundable: item.rooms?.[0]?.cancellationPolicies?.nonRefundable,
            quotaStatus: item.rooms?.[0]?.quotaStatusText,
            minNight: item.minNight,
            roomCount: hotelData?.roomCount || 0,
            restaurantCount: hotelData?.restaurantCount || 0,
            barCount: hotelData?.barCount || 0,
            themes: themesArray
          });
        }
      });

      // Tüm otelleri sakla
      this.allHotels = Array.from(uniqueHotels.values());
      
      // API'den gelen totalCount'u kontrol et
      const apiTotalCount = this.searchResults.totalCount || 0;
      const actualItemCount = this.allHotels.length;

      // ✅ Her sayfada 10 otel göster (this.pageSize = 10). API ne döndürürse döndürsün slice'la.
      // API tek seferde tüm sonuçları döndürüyorsa: totalCount=items.length
      const apiReturnsAll = actualItemCount > this.pageSize && (apiTotalCount === actualItemCount || apiTotalCount === 0);

      if (apiReturnsAll) {
        // Client-side pagination: tüm oteller geldi, 10'ar 10'ar göster
        this.useClientSidePagination = true;
        this.totalCount = actualItemCount;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        this.applyClientSidePagination();
      } else {
        // API pagination — ama 10'dan fazla geldiyse sadece 10'unu göster
        this.useClientSidePagination = false;
        if (this.searchResults.totalCount !== undefined) {
          this.totalCount = this.searchResults.totalCount;
        }
        if (this.searchResults.pageNumber !== undefined) {
          this.currentPage = this.searchResults.pageNumber;
        }
        // ❌ API'nin pageSize'ını kullanma — biz 10'da sabit tuttuğumuz için
        // this.pageSize = 10 sabit kalıyor
        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        // Geldiyse 10'dan fazla, ilk 10'u göster
        this.hotels = this.allHotels.slice(0, this.pageSize);
      }
    }
  }

  /**
   * Client-side pagination uygular
   */
  private applyClientSidePagination(): void {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.hotels = this.allHotels.slice(startIndex, endIndex);
  }

  viewHotel(hotel: Hotel): void {
    if (hotel.key) {
      this.searchService.setSelectedHotelPreview(hotel);
      const encodedProductKey = encodeURIComponent(hotel.key);
      this.router.navigate(['/hotel-detail', encodedProductKey]);
    }
  }

  /**
   * Minimum gece kısıtlaması durumunda ana sayfaya dön
   */
  goBackToSearch(): void {
    this.router.navigate(['/']);
  }

  showDetails(hotel: Hotel): void {
    if (hotel.key) {
      this.searchService.setSelectedHotelPreview(hotel);
      const encodedProductKey = encodeURIComponent(hotel.key);
      this.router.navigate(['/hotel-detail', encodedProductKey]);
    }
  }

  onSearch(): void {
    this.router.navigate(['/search'], {
      state: { searchResults: this.searchResults }
    });
  }

  loadHotels(): void {
    this.isLoading = true;
    this.hotelService.getHotels().subscribe({
      next: (data) => {
        this.hotels = data;
        this.isLoading = false;
        // searchResults artık SearchResponse tipinde, boolean atanamaz
      },
      error: (error) => {
        this.isLoading = false;
      }
    });
  }

  // Lokasyon seçimi
  selectLocation(location: any): void {
    this.searchParams.location = location.name;
  }

  // Yetişkin sayısı değişimi
  updateAdults(count: number): void {
    this.searchParams.adults = count;
  }

  // Çocuk sayısı değişimi
  updateChildren(count: number): void {
    this.searchParams.children = count;
  }

  // Oda sayısı değişimi
  updateRooms(count: number): void {
    this.searchParams.rooms = count;
  }

  // Gece sayısı hesapla
  getNightCount(checkIn: any, checkOut: any): number {
    if (!checkIn || !checkOut) return 0;
    const ci = new Date(checkIn);
    const co = new Date(checkOut);
    const diff = co.getTime() - ci.getTime();
    if (isNaN(diff) || diff <= 0) return 0;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  // Tarih değişimi
  updateDates(checkIn: Date, checkOut: Date): void {
    this.searchParams.checkIn = checkIn;
    this.searchParams.checkOut = checkOut;
  }

  // Arama yap
  search(): void {
    
    this.isLoading = true;
    this.hotelService.searchHotels(this.searchParams).subscribe({
      next: (data) => {
        this.hotels = data;
        this.isLoading = false;
        // searchResults artık SearchResponse tipinde, boolean atanamaz
      },
      error: (error) => {
        this.isLoading = false;
      }
    });
  }

  // Yeni form metodları
  private initializeSearchForm(): void {
    // Kaydedilmiş arama kriterlerini kontrol et
    const savedCriteria = this.searchService.getUserSearchCriteria();
    
    if (savedCriteria) {
      
      // Tarih kontrollü olarak al (tarihsiz aramada null kalır)
      let checkInDate: Date | null = null;
      let checkOutDate: Date | null = null;

      if (savedCriteria.checkInDate && !isNaN(new Date(savedCriteria.checkInDate).getTime())) {
        checkInDate = new Date(savedCriteria.checkInDate);
      }

      if (savedCriteria.checkOutDate && !isNaN(new Date(savedCriteria.checkOutDate).getTime())) {
        checkOutDate = new Date(savedCriteria.checkOutDate);
      }
      
      // Kaydedilmiş kriterleri kullan
      this.searchForm = this.fb.group({
        location: [savedCriteria.location || '', Validators.required],
        checkInDate: [checkInDate],
        checkOutDate: [checkOutDate],
        adults: [savedCriteria.adults || 2, [Validators.required, Validators.min(1), Validators.max(10)]],
        children: [savedCriteria.children || 0, [Validators.required, Validators.min(0), Validators.max(6)]],
        rooms: [savedCriteria.rooms || 1, [Validators.required, Validators.min(1), Validators.max(2)]],
        room2Adults: [savedCriteria.room2Adults || 1, [Validators.min(1), Validators.max(10)]],
        room2Children: [savedCriteria.room2Children || 0, [Validators.min(0), Validators.max(6)]]
      });
      
      // Seçili lokasyonu set et
      this.selectedLocation = savedCriteria.selectedLocation;
      
      // Çocuk yaşlarını set et
      this.childrenAges = savedCriteria.childrenAges ? [...savedCriteria.childrenAges] : [];
      this.room2ChildrenAges = savedCriteria.room2ChildrenAges ? [...savedCriteria.room2ChildrenAges] : [];
      
    } else {
      
      // Varsayılan değerler
      const checkIn = new Date();
      checkIn.setDate(checkIn.getDate() + 1);
      
      const checkOut = new Date();
      checkOut.setDate(checkOut.getDate() + 8);

      this.searchForm = this.fb.group({
        location: ['', Validators.required],
        checkInDate: [checkIn, Validators.required],
        checkOutDate: [checkOut, Validators.required],
        adults: [2, [Validators.required, Validators.min(1), Validators.max(10)]],
        children: [0, [Validators.required, Validators.min(0), Validators.max(6)]],
        rooms: [1, [Validators.required, Validators.min(1), Validators.max(2)]],
        room2Adults: [1, [Validators.min(1), Validators.max(10)]],
        room2Children: [0, [Validators.min(0), Validators.max(6)]]
      });
      
      // Başlangıçta çocuk sayısına göre yaş array'ini initialize et
      const initialChildrenCount = this.searchForm.get('children')?.value || 0;
      this.updateChildrenAges(initialChildrenCount);
    }
  }

  private initializeLocationAutocomplete(): void {
    // valueChanges + focus eventi merge — home sayfasıyla aynı mantık
    this.filteredLocations$ = merge(
      this.searchForm.get('location')!.valueChanges,
      this.locationFocus$
    ).pipe(
      switchMap(term => {
        if (typeof term === 'string' && term.length >= 1) {
          return this.searchLocations(term);
        }
        return of([]);
      })
    );
  }

  onLocationFocus(): void {
    const current = this.searchForm.get('location')?.value || '';
    this.locationFocus$.next(current);
  }

  private searchLocations(term: string): Observable<any[]> {
    const url = `${this.apiUrl}/Autocomplete/autocomplete`;
    const params = {
      term: term,
      lang: 'tr',
      limit: '5',
      type: 'all'
    };


    return this.http.get<any>(url, { params, withCredentials: true }).pipe(
      map(response => {
        return response.results || [];
      }),
      catchError(error => {
        return of([]);
      })
    );
  }

  onLocationSelect(location: any): void {
    this.selectedLocation = location;
    this.searchForm.patchValue({
      location: location.label
    });
    this.isMobileLocationOpen = false;
    this.mobileLocationSearch = '';
  }

  openMobileLocation(): void {
    if (window.innerWidth <= 991) {
      this.isMobileLocationOpen = true;
      this.isMobileSearchExpanded = true;
      this.mobileLocationSearch = '';
      setTimeout(() => {
        const input = document.querySelector('.mobile-location-overlay__input') as HTMLInputElement;
        input?.focus();
      }, 100);
    }
  }

  closeMobileLocation(): void {
    this.isMobileLocationOpen = false;
    this.mobileLocationSearch = '';
  }

  onMobileLocationInput(term: string): void {
    this.mobileLocationSearch = term;
    this.searchForm.patchValue({ location: term });
  }

  isMobileCalendarOpen = false;

  toggleCalendar(): void {
    this.isGuestSelectorOpen = false;
    this.isMobileGuestOpen = false;
    if (window.innerWidth <= 991) {
      this.isMobileCalendarOpen = !this.isMobileCalendarOpen;
      this.isCalendarOpen = false;
    } else {
      this.isCalendarOpen = !this.isCalendarOpen;
      this.isMobileCalendarOpen = false;
    }
  }

  closeCalendar(): void {
    this.isCalendarOpen = false;
  }

  closeMobileCalendar(): void {
    this.isMobileCalendarOpen = false;
  }

  onDateRangeSelected(event: { startDate: Date; endDate: Date }): void {
    this.searchForm.patchValue({
      checkInDate: event.startDate,
      checkOutDate: event.endDate
    });
    // Desktop'ta takvimi kapat, mobilde "Tamam"a basılsın
    if (window.innerWidth > 991) {
      this.isCalendarOpen = false;
    }
  }

  isMobileGuestOpen = false;

  toggleGuestSelector(): void {
    this.isCalendarOpen = false;
    if (window.innerWidth <= 991) {
      // Mobilde tam ekran overlay aç
      this.isMobileGuestOpen = !this.isMobileGuestOpen;
      this.isGuestSelectorOpen = false;
    } else {
      // Desktop'ta inline dropdown aç
      this.isGuestSelectorOpen = !this.isGuestSelectorOpen;
      this.isMobileGuestOpen = false;
    }
  }

  closeMobileGuest(): void {
    this.isMobileGuestOpen = false;
  }

  /**
   * Giriş ve çıkış tarihleri seçilip seçilmediğini kontrol eder
   */
  isDateSelected(): boolean {
    const checkInDate = this.searchForm.get('checkInDate')?.value;
    const checkOutDate = this.searchForm.get('checkOutDate')?.value;
    return !!(checkInDate && checkOutDate);
  }

  /**
   * Tarihsiz arama yapılıp yapılmadığını kontrol eder
   */
  isSearchWithoutDate(): boolean {
    return !this.isDateSelected();
  }

  updateGuestCount(type: 'adults' | 'children' | 'rooms', increment: boolean): void {
    const control = this.searchForm.get(type);
    if (!control) return;

    const currentValue = control.value;
    const newValue = increment ? currentValue + 1 : currentValue - 1;
    
    let min, max;
    
    if (type === 'adults') {
      min = 1;
      max = 10;
      
    } else if (type === 'children') {
      min = 0;
      max = 6;
    } else if (type === 'rooms') {
      min = 1;
      max = 2;
    }

    if (min !== undefined && max !== undefined && newValue >= min && newValue <= max) {
      control.setValue(newValue);
      
      // Çocuk sayısı değiştiğinde yaş array'ini güncelle
      if (type === 'children') {
        this.updateChildrenAges(newValue);
      }

      // Oda sayısı 2 olduğunda 2. oda değerlerini initialize et, 1'e düşerse sıfırla
      if (type === 'rooms') {
        if (newValue === 2) {
          if ((this.searchForm.get('room2Adults')?.value ?? 0) < 1) {
            this.searchForm.get('room2Adults')?.setValue(1);
          }
          if ((this.searchForm.get('room2Children')?.value ?? -1) < 0) {
            this.searchForm.get('room2Children')?.setValue(0);
          }
        } else if (newValue === 1) {
          this.searchForm.get('room2Adults')?.setValue(1);
          this.searchForm.get('room2Children')?.setValue(0);
          this.room2ChildrenAges = [];
        }
      }
    }
  }

  updateChildrenAges(childrenCount: number): void {
    if (childrenCount > this.childrenAges.length) {
      for (let i = this.childrenAges.length; i < childrenCount; i++) {
        this.childrenAges.push(5);
      }
    } else if (childrenCount < this.childrenAges.length) {
      this.childrenAges = this.childrenAges.slice(0, childrenCount);
    }
  }

  updateChildrenAgesRoom2(childrenCount: number): void {
    if (childrenCount > this.room2ChildrenAges.length) {
      for (let i = this.room2ChildrenAges.length; i < childrenCount; i++) {
        this.room2ChildrenAges.push(5);
      }
    } else if (childrenCount < this.room2ChildrenAges.length) {
      this.room2ChildrenAges = this.room2ChildrenAges.slice(0, childrenCount);
    }
  }

  updateGuestCountRoom2(type: 'room2Adults' | 'room2Children', increment: boolean): void {
    const control = this.searchForm.get(type);
    if (!control) return;

    const currentValue = control.value;
    const newValue = increment ? currentValue + 1 : currentValue - 1;

    let min = type === 'room2Adults' ? 1 : 0;
    let max = type === 'room2Adults' ? 10 : 6;

    if (newValue >= min && newValue <= max) {
      control.setValue(newValue);
      if (type === 'room2Children') {
        this.updateChildrenAgesRoom2(newValue);
      }
    }
  }

  addRoom(): void {
    const current = this.searchForm.get('rooms')?.value ?? 1;
    if (current < 2) {
      this.updateGuestCount('rooms', true);
    }
  }

  removeRoom(): void {
    this.searchForm.get('rooms')?.setValue(1);
    this.searchForm.get('room2Adults')?.setValue(1);
    this.searchForm.get('room2Children')?.setValue(0);
    this.room2ChildrenAges = [];
  }

  getTotalAdults(): number {
    const room1 = this.searchForm.get('adults')?.value ?? 0;
    const rooms = this.searchForm.get('rooms')?.value ?? 1;
    if (rooms === 2) {
      const room2 = this.searchForm.get('room2Adults')?.value ?? 0;
      return room1 + room2;
    }
    return room1;
  }

  getTotalChildren(): number {
    const room1 = this.searchForm.get('children')?.value ?? 0;
    const rooms = this.searchForm.get('rooms')?.value ?? 1;
    if (rooms === 2) {
      const room2 = this.searchForm.get('room2Children')?.value ?? 0;
      return room1 + room2;
    }
    return room1;
  }

  onChildAgeChange(event: Event, index: number): void {
    const target = event.target as HTMLSelectElement;
    const age = parseInt(target.value, 10);
    this.updateChildAge(index, age);
  }

  onChildAgeChangeRoom2(event: Event, index: number): void {
    const target = event.target as HTMLSelectElement;
    const age = parseInt(target.value, 10);
    this.updateChildAgeRoom2(index, age);
  }

  updateChildAge(index: number, age: number): void {
    if (index >= 0 && index < this.childrenAges.length) {
      this.childrenAges[index] = age;
    }
  }

  incrementChildAge(index: number, delta: number): void {
    const current = this.childrenAges[index] ?? 0;
    const next = Math.max(0, Math.min(17, current + delta));
    this.childrenAges[index] = next;
  }

  updateChildAgeRoom2(index: number, age: number): void {
    if (index >= 0 && index < this.room2ChildrenAges.length) {
      this.room2ChildrenAges[index] = age;
    }
  }

  incrementChildAgeRoom2(index: number, delta: number): void {
    const current = this.room2ChildrenAges[index] ?? 0;
    const next = Math.max(0, Math.min(17, current + delta));
    this.room2ChildrenAges[index] = next;
  }

  scrollToSearchAndOpenCalendar(): void {
    const searchEl = document.querySelector('.mainSearch');
    if (searchEl) {
      searchEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setTimeout(() => {
      this.isCalendarOpen = true;
    }, 400);
  }

  performSearch(): void {
    console.log('[Filter-Debug] performSearch ÇAĞRILDI ✓ BUILD-VERSION=2026-04-21-v3');
    this.fromNav = false;

    // Zaten arama yapılıyorsa mükerrer isteği engelle
    if (this.isSearching) {
      console.log('[Filter-Debug] ERKEN ÇIKIŞ: isSearching=true');
      return;
    }

    if (this.searchForm.invalid) {
      console.log('[Filter-Debug] ERKEN ÇIKIŞ: form invalid', this.searchForm.errors);
      return;
    }

    if (!this.selectedLocation) {
      console.log('[Filter-Debug] ERKEN ÇIKIŞ: selectedLocation yok');
      return;
    }

    // ⭐ KRİTİK: Yeni arama öncesi eski passenger key'leri ve arama state'ini temizle
    this.searchService.clearPassengerState();

    // Tarihsiz arama kontrolü: formda tarih var mı?
    const checkInDate = this.searchForm.get('checkInDate')?.value;
    const checkOutDate = this.searchForm.get('checkOutDate')?.value;
    const hasCurrentDates = checkInDate && checkOutDate;
    const shouldBeDateless = !hasCurrentDates;
    
    if (shouldBeDateless) {
      this.showDateRequiredWarning = false;
      this.isDatelessSearch = true;
      // Form orijinal değerleri göstermeye devam eder
      // API'ye 1 yetişkin gönderilir (aşağıda searchRequest.rooms'da)
    } else {
      this.showDateRequiredWarning = false;
      this.isDatelessSearch = false;
    }

    this.isSearching = true; // Arama başladı
    this.isLoading = true;
    // Yeni arama başlatıldığında pagination'ı sıfırla
    this.requestKey = null;
    this.currentPage = 1;
    this.useClientSidePagination = false;
    this.hotels = [];
    this.allHotels = [];

    let parentId = 0;
    if (this.selectedLocation.parentId) {
      const parsedParentId = parseInt(this.selectedLocation.parentId, 10);
      if (!isNaN(parsedParentId)) {
        parentId = parsedParentId;
      }
    }

    const locationType = this.getLocationType(this.selectedLocation.type);
    const locationId = parseInt(this.selectedLocation.value?.toString() || this.selectedLocation.id?.toString() || '0', 10);

    // Kıbrıs oteli veya Hotel tipinde ise özel işlem
    const isCyprusOrHotel = this.selectedLocation.type?.toLowerCase() === 'cyprus' || 
                            this.selectedLocation.type?.toLowerCase() === 'hotel';
    
    // Kıbrıs oteli için masterHotelId varsa onu kullan, yoksa id'yi kullan
    const hotelIdValue = isCyprusOrHotel 
      ? parseInt(this.selectedLocation.masterHotelId?.toString() || this.selectedLocation.id?.toString() || '0', 10)
      : 0;

    const destination = {
      destinationId: isCyprusOrHotel ? 0 : locationId, // Hotel/Cyprus ise 0, değilse locationId
      hotelId: isCyprusOrHotel ? hotelIdValue : 0, // Hotel/Cyprus ise hotelIdValue, değilse 0
      destinationLabel: this.selectedLocation.label,
      destinationParentId: isCyprusOrHotel ? 0 : parentId, // Kıbrıs için parentId 0
      locationType: isCyprusOrHotel ? 5 : locationType, // Kıbrıs için locationType 5 (Hotel)
    };

    const searchRequest: SearchRequest = {
      currency: 'TRY',
      countryCode: 'TR',
      destinations: [destination],
      pageNumber: 1,
      pageSize: 10,
      includeRoomPrices: false,
    } as SearchRequest;

    if (shouldBeDateless) {
      // Tarihsiz arama: tarihleri null gönder, backend cached fiyat döner
      searchRequest.checkInDate = null as any;
      searchRequest.checkOutDate = null as any;
      searchRequest.rooms = [{ passengers: [{ age: 0, birthday: null, gender: null, passengerType: 0 }] }];
    } else {
      const requestCheckInDate = new Date(this.searchForm.get('checkInDate')?.value);
      requestCheckInDate.setHours(0, 0, 0, 0);
      const requestCheckOutDate = new Date(this.searchForm.get('checkOutDate')?.value);
      requestCheckOutDate.setHours(0, 0, 0, 0);
      searchRequest.checkInDate = toLocalISOString(requestCheckInDate);
      searchRequest.checkOutDate = toLocalISOString(requestCheckOutDate);

      searchRequest.rooms = this.createRooms();
    }


    // Kullanıcı arama kriterlerini kaydet
    const userCriteria: UserSearchCriteria = {
      location: this.searchForm.get('location')?.value,
      selectedLocation: this.selectedLocation,
      checkInDate: this.searchForm.get('checkInDate')?.value,
      checkOutDate: this.searchForm.get('checkOutDate')?.value,
      adults: this.searchForm.get('adults')?.value,
      children: this.searchForm.get('children')?.value,
      rooms: this.searchForm.get('rooms')?.value,
      room2Adults: this.searchForm.get('room2Adults')?.value,
      room2Children: this.searchForm.get('room2Children')?.value,
      childrenAges: [...this.childrenAges],
      room2ChildrenAges: [...this.room2ChildrenAges]
    };
    this.searchService.setUserSearchCriteria(userCriteria);

    // SearchService üzerinden arama yap - response service'in tap() içinde BehaviorSubject'e yazılır
    // searchResults$ subscription'ı otomatik olarak processSearchResults() çağırır
    this.searchService.searchHotels(searchRequest).subscribe({
      next: () => {
        this.isSearching = false;
      },
      error: () => {
        this.isLoading = false;
        this.isSearching = false;
      }
    });

    // Filtre metadata'sını PARALEL olarak çek (Gordios önerisi — RequestKey beklemeden)
    // Tarihsiz aramalarda GetFilters çalışmaz, atlıyoruz
    console.log('[Filter-Debug] shouldBeDateless:', shouldBeDateless,
      'checkInDate:', searchRequest.checkInDate,
      'checkOutDate:', searchRequest.checkOutDate);
    if (!shouldBeDateless && searchRequest.checkInDate && searchRequest.checkOutDate) {
      console.log('[Filter-Debug] loadFilters çağırılıyor');
      this.loadFilters(destination, searchRequest);
    } else {
      console.log('[Filter-Debug] loadFilters ATLANDI');
      this.filterResult = null;
    }
  }

  /**
   * searchService cache/state'inden parametre bulup filtreleri yükler.
   * performSearch çalışmadığı senaryolarda (URL refresh, back nav vs.) kullanılır.
   */
  private tryLoadFiltersFromState(): void {
    const savedParams = this.searchService.getLastSearchParams();
    if (!savedParams) { console.log('[Filter-Debug] tryLoadFilters: savedParams yok'); return; }
    if (!savedParams.checkInDate || !savedParams.checkOutDate) {
      console.log('[Filter-Debug] tryLoadFilters: tarih yok, atlıyorum');
      return;
    }
    const destination = savedParams.destinations?.[0];
    if (!destination) { console.log('[Filter-Debug] tryLoadFilters: destination yok'); return; }

    console.log('[Filter-Debug] tryLoadFilters ÇALIŞTI — GetFilters tetikleniyor');
    this.loadFilters(destination, savedParams);
  }

  /**
   * Gordios GetFilters endpoint'ini çağırır. Filtre sidebar'ının dinamik seçeneklerini doldurur.
   * Destination / Tarih / Yolcu değişmediği sürece tekrar çağrılmaz (Gordios önerisi).
   */
  private loadFilters(destination: any, searchRequest: SearchRequest): void {
    if (!searchRequest.checkInDate || !searchRequest.checkOutDate) return;

    const req: HotelFilterRequest = {
      checkInDate: searchRequest.checkInDate,
      checkOutDate: searchRequest.checkOutDate,
      countryCode: searchRequest.countryCode ?? 'TR',
      destination: {
        id: destination.destinationId || destination.hotelId || 0,
        label: destination.destinationLabel ?? '',
        parentId: destination.destinationParentId || null,
      },
      rooms: (searchRequest.rooms ?? []).map(r => ({
        passengers: r.passengers.map(p => ({
          age: p.age ?? 0,
          passengerType: p.passengerType === 1 ? 'Child' : 'Adult',
          // Gordios PassengerGender enum'ında sadece Male/Female var, Unknown yok.
          // GetFilters için cinsiyet bilgisi önemli değil — varsayılan Male.
          gender: p.gender === 1 ? 'Female' : 'Male',
          name: '',
          surname: '',
        })),
      })),
      // Gordios'a hangi filtre metadata'larının dönmesini istediğimizi açıkça söylüyoruz.
      // Boş/null gönderirsek Gordios varsayılan olarak hiç filtre döndürmeyebilir.
      hotelFilterIncludeItems: [
        'PriceRangesAndAvailableTypeCodes',
        'CampaignTypeCodes',
        'HotelNames',
        'MealTypeCodes',
        'LcationIds', // Gordios schema'sında typo — doğru yazım bu
        'FoodAndDrinkCodes',
        'NearDistanceCodes',
        'HotelThemeCodes',
        'HotelTypeCodes',
        'HotelFacilityCodes',
        'HotelRoomFacilityCodes',
        'DiscountTypeCodes',
        'HygienicalMeasureCodes',
        'CategoryTypeCodes',
      ],
    };

    this.filterLoading = true;
    this.hotelFilterService.getFilters(req).subscribe({
      next: (res) => {
        this.filterLoading = false;
        console.log('[GetFilters] Response:', res);
        if (res?.success && res.result) {
          this.filterResult = res.result;
          console.log('[GetFilters] Result keys:', Object.keys(res.result));
        }
      },
      error: (err) => {
        this.filterLoading = false;
        console.error('[GetFilters] Error:', err);
      },
    });
  }

  /**
   * Kullanıcı filtre veya sıralama uyguladı — yeni Search çağrısı at (RequestKey GÖNDERME).
   */
  onFiltersApplied(payload: { filters: SearchFilterItem; orderBy: 'Average' | 'PriceAscending' | 'PriceDescending' }): void {
    const { filters, orderBy } = payload;
    this.currentFilters = filters;

    const savedParams = this.searchService.getLastSearchParams();
    if (!savedParams) return;

    // RequestKey'i sıfırla — yeni sonuç seti için zorunlu (Gordios kuralı)
    this.requestKey = null;
    this.currentPage = 1;
    this.useClientSidePagination = false;
    this.isLoading = true;
    this.hotels = [];
    this.allHotels = [];

    const hasAnyFilter = Object.keys(filters).length > 0;

    const req: SearchRequest = {
      ...savedParams,
      pageNumber: 1,
      pageSize: this.pageSize,
      requestKey: undefined, // ÇOK ÖNEMLİ — eski RequestKey kullanılmamalı
      searchFilterItem: hasAnyFilter ? filters : undefined,
      orderBy,
    };

    this.searchService.searchHotels(req).subscribe({
      next: () => { this.isLoading = false; },
      error: () => { this.isLoading = false; },
    });

    // Mobilde sidebar'ı kapat ve üste zıpla
    this.isFilterSidebarOpen = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  toggleFilterSidebar(): void {
    this.isFilterSidebarOpen = !this.isFilterSidebarOpen;
  }

  closeFilterSidebar(): void {
    this.isFilterSidebarOpen = false;
  }

  get hasActiveFilters(): boolean {
    return Object.keys(this.currentFilters).length > 0;
  }

  /**
   * RequestKey kullanarak sayfa değiştirir (Pagination)
   * @param pageNumber Gidilecek sayfa numarası
   */
  goToPage(pageNumber: number): void {
    if (pageNumber < 1 || pageNumber > this.totalPages) {
      return;
    }

    if (pageNumber === this.currentPage) {
      return;
    }

    // Anında en üste zıpla — loading/skeleton tepede görünsün, data beklemesin
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });

    // Client-side pagination kullanılıyorsa
    if (this.useClientSidePagination) {
      this.currentPage = pageNumber;
      this.applyClientSidePagination();
      return;
    }

    // API pagination
    this.isPaginationLoading = true;

    let paginationRequest: SearchRequest;

    if (this.requestKey) {
      // RequestKey varsa cached pagination (hızlı)
      paginationRequest = {
        requestKey: this.requestKey,
        pageNumber: pageNumber,
        pageSize: this.pageSize
      };
    } else {
      // RequestKey yoksa orijinal arama parametreleriyle sayfa değiştir
      const savedParams = this.searchService.getLastSearchParams();
      if (!savedParams) {
        this.isPaginationLoading = false;
        return;
      }
      paginationRequest = { ...savedParams, pageNumber: pageNumber, pageSize: this.pageSize };
    }

    this.http.post<SearchResponse>(`${this.apiUrl}/Search`, paginationRequest, {
      withCredentials: true
    }).subscribe({
      next: (response: SearchResponse) => {
        if (response.requestKey) {
          this.requestKey = response.requestKey;
        }
        this.searchResults = response;
        this.searchService.setSearchResults(response);
        this.processSearchResults();
        this.isPaginationLoading = false;
      },
      error: () => {
        this.isPaginationLoading = false;
      }
    });
  }

  /**
   * Sonraki sayfaya git
   */
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  /**
   * Önceki sayfaya git
   */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  /**
   * Sayfa numaralarını döndürür (UI için)
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    if (this.totalPages <= maxVisiblePages) {
      // Toplam sayfa sayısı az ise hepsini göster
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Çok sayfa varsa, mevcut sayfa etrafındakileri göster
      let startPage = Math.max(1, this.currentPage - 2);
      let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);
      
      // Sona yakınsa başlangıcı ayarla
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }

  /**
   * Lokasyon etiketini döndürür
   * Önce selectedLocation'dan, yoksa searchResults'tan alır
   */
  getLocationLabel(): string {
    // Önce selectedLocation'dan kontrol et
    if (this.selectedLocation?.label) {
      return '- ' + this.selectedLocation.label;
    }
    
    // SearchResults'tan destination label al
    const destinationLabel = this.searchResults?.searchCriteria?.destinations?.[0]?.destinationLabel;
    if (destinationLabel) {
      return '- ' + destinationLabel;
    }
    
    return '';
  }

  private getLocationType(type: string): number {
    switch (type.toLowerCase()) {
      case 'city':
        return 0;
      case 'country':
        return 1;
      case 'continent':
        return 2;
      case 'district':
        return 3;
      case 'area':
        return 4;
      case 'hotel':
      case 'cyprus': // Kıbrıs otelleri de Hotel tipi olarak işlenir
        return 5;
      case 'category':
        return 6;
      default:
        return 0;
    }
  }

  private createRooms(): { passengers: { age: number; birthday: string | null; gender: number | null; passengerType: number }[] }[] {
    const rooms: { passengers: { age: number; birthday: string | null; gender: number | null; passengerType: number }[] }[] = [];
    const numberOfRooms = this.searchForm.get('rooms')?.value ?? 1;
    const adultsRoom1 = this.searchForm.get('adults')?.value ?? 0;
    const childrenRoom1 = this.searchForm.get('children')?.value ?? 0;
    const adultsRoom2 = numberOfRooms === 2 ? (this.searchForm.get('room2Adults')?.value ?? 0) : 0;
    const childrenRoom2 = numberOfRooms === 2 ? (this.searchForm.get('room2Children')?.value ?? 0) : 0;

    // 1. Oda
    const room1Passengers: { age: number; birthday: string | null; gender: number | null; passengerType: number }[] = [];
    
    // 1. oda yetişkinler
    for (let i = 0; i < adultsRoom1; i++) {
      room1Passengers.push({
        age: 0,
        birthday: null,
        gender: null,
        passengerType: 0,
      });
    }

    // 1. oda çocukları
    for (let i = 0; i < childrenRoom1; i++) {
      const childAge = this.childrenAges[i] || 5;
      room1Passengers.push({
        age: childAge,
        birthday: null,
        gender: null,
        passengerType: 1,
      });
    }

    rooms.push({ passengers: room1Passengers });

    // 2. Oda (varsa)
    if (numberOfRooms === 2) {
      const room2Passengers: { age: number; birthday: string | null; gender: number | null; passengerType: number }[] = [];
      
      // 2. oda yetişkinler
      for (let i = 0; i < adultsRoom2; i++) {
        room2Passengers.push({
          age: 0,
          birthday: null,
          gender: null,
          passengerType: 0,
        });
      }

      // 2. oda çocukları
      for (let i = 0; i < childrenRoom2; i++) {
        const childAge = this.room2ChildrenAges[i] || 5;
        room2Passengers.push({
          age: childAge,
          birthday: null,
          gender: null,
          passengerType: 1,
        });
      }

      rooms.push({ passengers: room2Passengers });
    }

    return rooms;
  }

  /**
   * İptal tarihinin geçerli olup olmadığını kontrol eder
   * 01.01.0001 gibi sistem hatalı tarihleri filtreler
   */
  isValidCancellationDate(dateString: string | null): boolean {
    if (!dateString) return false;
    
    const date = new Date(dateString);
    // Geçersiz tarih veya yıl 1900'den küçükse false döndür
    if (isNaN(date.getTime()) || date.getFullYear() < 1900) {
      return false;
    }
    return true;
  }

  private toThumbnail(url: string | undefined): string | null {
    if (!url) return null;
    const dotIndex = url.lastIndexOf('.');
    if (dotIndex === -1) return url;
    return url.substring(0, dotIndex) + '-600' + url.substring(dotIndex);
  }
}